import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const loanPrograms = [
    t('loanTypes.fixFlip'),
    t('loanTypes.bridge'),
    t('loanTypes.rental'),
    t('loanTypes.commercial'),
    t('loanTypes.refinance'),
  ];

  return (
    <footer className="bg-slate-900 text-white footer-pattern" data-testid="footer">
      {/* Main Footer */}
      <div className="container-main py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <div className="flex flex-col items-start leading-none">
                <span className="font-heading text-xl font-bold text-white tracking-tight">GANN</span>
                <div className="w-full h-px bg-amber-500 my-1"></div>
                <span className="font-heading text-[10px] font-semibold text-amber-500 tracking-[0.25em] uppercase">Private Lending</span>
              </div>
            </div>
            <p className="font-body text-slate-400 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="space-y-3">
              <a
                href="tel:424-303-2130"
                className="flex items-center space-x-3 text-slate-300 hover:text-amber-500 transition-colors duration-200"
                data-testid="footer-phone"
              >
                <Phone size={18} />
                <span className="font-body">424-303-2130</span>
              </a>
              <a
                href="mailto:ganninvesting@gmail.com"
                className="flex items-center space-x-3 text-slate-300 hover:text-amber-500 transition-colors duration-200"
                data-testid="footer-email"
              >
                <Mail size={18} />
                <span className="font-body">ganninvesting@gmail.com</span>
              </a>
              <div className="flex items-start space-x-3 text-slate-300">
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <span className="font-body text-sm">
                  1901 Avenue of the Stars #200<br />
                  Los Angeles, CA 90067
                </span>
              </div>
            </div>
          </div>

          {/* Loan Programs */}
          <div>
            <h4 className="font-heading text-lg font-bold mb-6">{t('nav.loanPrograms')}</h4>
            <ul className="space-y-3">
              {loanPrograms.map((item) => (
                <li key={item}>
                  <Link
                    to="/loans"
                    className="font-body text-slate-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-bold mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="font-body text-slate-400 hover:text-white transition-colors duration-200 text-sm">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/how-it-works" className="font-body text-slate-400 hover:text-white transition-colors duration-200 text-sm">
                  {t('nav.howItWorks')}
                </Link>
              </li>
              <li>
                <Link to="/deals" className="font-body text-slate-400 hover:text-white transition-colors duration-200 text-sm">
                  Recent Deals
                </Link>
              </li>
              <li>
                <Link to="/blog" className="font-body text-slate-400 hover:text-white transition-colors duration-200 text-sm">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/about" className="font-body text-slate-400 hover:text-white transition-colors duration-200 text-sm">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="font-body text-slate-400 hover:text-white transition-colors duration-200 text-sm">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Get Funded */}
          <div>
            <h4 className="font-heading text-lg font-bold mb-6">{t('footer.getFunded')}</h4>
            <p className="font-body text-slate-400 text-sm mb-6">
              {t('footer.getFundedDesc')}
            </p>
            <Link
              to="/contact"
              className="inline-block btn-gold rounded-none text-sm"
              data-testid="footer-get-funded"
            >
              {t('footer.submitInquiry')}
            </Link>
          </div>
        </div>
      </div>

      {/* Compliance Disclaimer */}
      <div className="border-t border-slate-800">
        <div className="container-main py-6">
          <p className="font-body text-xs text-slate-500 leading-relaxed" data-testid="compliance-disclaimer">
            {t('footer.disclaimer')}
          </p>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-slate-800">
        <div className="container-main py-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <p className="font-body text-xs text-slate-500">
              © {currentYear} Gann Private Lending. {t('footer.rights')}
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="font-body text-xs text-slate-500 hover:text-slate-300 transition-colors duration-200">
                {t('footer.privacy')}
              </Link>
              <Link to="/terms" className="font-body text-xs text-slate-500 hover:text-slate-300 transition-colors duration-200">
                {t('footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
